import { AlphaHttpClient, LanguagesService, SessionTimeoutDialogComponent, UserService } from '@alpha-core/platform-ui';
import { Injectable, inject } from '@angular/core';
import { Idle } from '@ng-idle/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SessionTimeoutService {
    private idle = inject<Idle>(Idle);
    private dialogService = inject(DialogService);
    private userService = inject(UserService);
    private languageService = inject(LanguagesService);

    private readonly DEFAULT_SESSION_EXPIRE_TIME = 1 * 24 * 60 * 60 * 1000; // 1 day
    private readonly DEFAULT_TIMEOUT_TIME = 5 * 60 * 1000; // 5 minutes
    private dialogRef: DynamicDialogRef | null = null;
    remainingTime: number = 0;
    private timeRemainingSubject = new BehaviorSubject<number>(0);

    private httpClient = inject(AlphaHttpClient);

    /**
     * Initialize the session timeout monitoring
     * @param idleTime
     * @param isAdmin
     */
    public initSessionTimeout(
        sessionExpireTime: number = this.DEFAULT_SESSION_EXPIRE_TIME,
        sessionExpireWaringTime: number = this.DEFAULT_TIMEOUT_TIME,
        isAdmin: boolean = false,
    ): void {
        const sessionMaxAge = (Number(sessionExpireTime) - 10000 - Number(sessionExpireWaringTime)) / 1000;
        if (!sessionMaxAge || sessionMaxAge <= 0) {
            console.warn('[SessionTimeout] Skipped initialization due to invalid sessionMaxAge:', sessionMaxAge);
            return;
        }
        this.idle.setIdle(sessionMaxAge);
        this.idle.setTimeout(sessionExpireWaringTime / 1000);
        this.idle.setInterrupts([]);
        this.idle.onIdleStart.subscribe(() => this.onIdleStart());
        this.idle.onTimeoutWarning.subscribe((seconds: number) => this.onTimeoutWarning(seconds));
        this.idle.onTimeout.subscribe(() => this.onTimeout());

        this.idle.watch();
    }

    public resetSession(): void {
        this.remainingTime = 0;
        this.idle.watch();
    }

    public stopMonitoring(): void {
        this.closeWarningDialog();
        this.idle.stop();
    }

    private onIdleStart(): void {
        this.showWarningDialog();
    }

    private onTimeoutWarning(seconds: number): void {
        this.remainingTime = seconds;
        this.timeRemainingSubject.next(seconds);
    }

    private onTimeout(): void {
        this.stopMonitoring();
        this.closeWarningDialog();
        this.handleSessionExpiration();
    }

    private showWarningDialog(): void {
        if (this.dialogRef) return;

        this.dialogRef = this.dialogService.open(SessionTimeoutDialogComponent, {
            header: this.languageService.getTranslation('default-cm-confirm') || 'Confirm',
            closable: false,
            closeOnEscape: false,
            dismissableMask: false,
            data: {
                timeRemaining: this.timeRemainingSubject.asObservable(),
                isSessionExpired: false,
                imagePath: 'assets/images/session-timeout.svg',
            },
        });

        this.dialogRef.onClose.subscribe(async (result: string) => {
            if (result === 'continue') {
                this.closeWarningDialog();
                await lastValueFrom(this.userService.resfreshCookie()).then(() => {
                    this.idle.watch();
                });
            } else if (result === 'logout') {
                this.logout();
            }
            this.dialogRef = null;
        });
    }

    async closeWarningDialog() {
        if (this.dialogRef) {
            this.dialogRef.destroy();
            this.dialogRef = null;
        }
    }

    async handleSessionExpiration(): Promise<void> {
        if (!this.dialogRef) {
            const dialogRef1 = this.dialogService.open(SessionTimeoutDialogComponent, {
                header:
                    this.languageService.getTranslation('default-platform-sessionExpiredTitle') || 'Session Expired',
                closable: false,
                closeOnEscape: false,
                dismissableMask: false,
                data: {
                    isSessionExpired: true,
                    imagePath: 'assets/images/session-expired.svg',
                },
            });
            dialogRef1?.onClose.subscribe((result: string) => {
                if (result === 'login') {
                    this.loging();
                }
                this.dialogRef = null;
            });
        }
    }

    private logout(): void {
        this.stopMonitoring();
        this.userService.userLogOut();
    }

    private loging(): void {
        this.stopMonitoring();
        this.userService.userLogin();
    }

    unauthorizedListenerFactory() {
        window.addEventListener('alpha-unauthorized', (event: Event) => {
            const customEvent = event as CustomEvent;
            console.log('Unauthorized event received:', customEvent.detail);
            this.handleSessionExpiration();
        });
    }

    async initializeSessionTimeout(isAdmin: boolean = false) {
        this.userService.baseURL = isAdmin
            ? window.environments.ALPHA_UI_ADMIN_SERVICE_URL
            : window.environments.ALPHA_UI_AUTH_SERVICE_URL;
        if (window.environments.DEV_MODE === 'false' && window.environments.ENABLE_IDLE_SESSION_TIMEOUT === 'true') {
            const maxAgeDetails: { sessionMaxAge: number; sessionTimeout: number } = await lastValueFrom(
                this.userService.fetchSessionMaxAge(),
            );
            this.initSessionTimeout(maxAgeDetails.sessionMaxAge, maxAgeDetails.sessionTimeout, isAdmin);
        }
    }
}
